import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAYS = [
  { k: 'mon', l: 'Mon' }, { k: 'tue', l: 'Tue' }, { k: 'wed', l: 'Wed' },
  { k: 'thu', l: 'Thu' }, { k: 'fri', l: 'Fri' }, { k: 'sat', l: 'Sat' }, { k: 'sun', l: 'Sun' },
];
const TYPE_COLORS = {
  school: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  tuition: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  hobby: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
  sport: 'bg-green-500/10 text-green-600 border-green-500/30',
  other: 'bg-muted text-muted-foreground border-border',
};

export const TimetableTab = ({ api, kid }) => {
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ day: 'mon', start: '08:00', end: '09:00', title: '', type: 'school', location: '' });

  const fetchEntries = useCallback(async () => {
    try { const r = await api.get(`/kids/${kid.kid_id}/timetable`); setEntries(r.data); } catch (e) { console.error(e); }
  }, [api, kid.kid_id]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/kids/${kid.kid_id}/timetable`, form);
      toast.success('Added to timetable');
      setShowForm(false);
      setForm({ day: 'mon', start: '08:00', end: '09:00', title: '', type: 'school', location: '' });
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/kids/timetable/${id}`);
    fetchEntries();
  };

  return (
    <div className="space-y-4" data-testid="kids-timetable-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{kid.name}'s weekly schedule — school, tuition, hobbies & sports</p>
        <button onClick={() => setShowForm(true)} data-testid="add-timetable-btn"
          className="px-3 py-2 rounded-full bg-[#FF6B35] text-white text-sm font-semibold flex items-center gap-1.5"><Plus size={14} /> Add Class</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {DAYS.map(d => {
          const dayEntries = entries.filter(e => e.day === d.k).sort((a, b) => a.start.localeCompare(b.start));
          const isToday = new Date().getDay() === (DAYS.findIndex(x => x.k === d.k) + 1) % 7;
          return (
            <div key={d.k} className={`glass-card p-3 min-h-[120px] ${isToday ? 'ring-2 ring-[#FF6B35]/40' : ''}`} data-testid={`timetable-day-${d.k}`}>
              <p className={`text-xs font-bold uppercase mb-2 ${isToday ? 'text-[#FF6B35]' : 'text-muted-foreground'}`}>{d.l}{isToday && ' · Today'}</p>
              <div className="space-y-1.5">
                {dayEntries.map(e => (
                  <div key={e.entry_id} className={`rounded-lg border p-2 text-xs group relative ${TYPE_COLORS[e.type] || TYPE_COLORS.other}`}>
                    <p className="font-semibold leading-tight">{e.title}</p>
                    <p className="opacity-80">{e.start}–{e.end}</p>
                    {e.location && <p className="opacity-70 truncate">{e.location}</p>}
                    <button onClick={() => handleDelete(e.entry_id)}
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-black/10"><Trash size={11} /></button>
                  </div>
                ))}
                {dayEntries.length === 0 && <p className="text-[11px] text-muted-foreground/60">Free day</p>}
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-card rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="timetable-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold">Add Class</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <input data-testid="tt-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                placeholder="e.g. Maths Tuition" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
              <div className="grid grid-cols-3 gap-2">
                <select value={form.day} onChange={e => setForm({ ...form, day: e.target.value })} data-testid="tt-day-select"
                  className="px-2 py-2 rounded-lg border border-border bg-background text-sm">
                  {DAYS.map(d => <option key={d.k} value={d.k}>{d.l}</option>)}
                </select>
                <input type="time" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })}
                  className="px-2 py-2 rounded-lg border border-border bg-background text-sm" />
                <input type="time" value={form.end} onChange={e => setForm({ ...form, end: e.target.value })}
                  className="px-2 py-2 rounded-lg border border-border bg-background text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                  className="px-2 py-2 rounded-lg border border-border bg-background text-sm">
                  <option value="school">School</option><option value="tuition">Tuition</option>
                  <option value="hobby">Hobby</option><option value="sport">Sport</option><option value="other">Other</option>
                </select>
                <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                  placeholder="Location" className="px-2 py-2 rounded-lg border border-border bg-background text-sm" />
              </div>
              <button type="submit" data-testid="tt-save-btn" className="w-full px-4 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">Add</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
